import type { SimulationResult } from "../../lib/api";
import { cn } from "../../lib/cn";
import { IntendedPathCard } from "./IntendedPathCard";
import { CostBreakdown } from "./plan/CostBreakdown";
import { SimulationStrip } from "./plan/SimulationStrip";
import { TriageList } from "./TriageList";

export function SimulationBlock({ data }: { data: SimulationResult }) {
	if (data.ok === false) return <FailureBlock data={data} />;
	return (
		<div className="space-y-2">
			<SimulationStrip plan={data.plan} />
			<CostBreakdown totals={data.totals} bundles={data.bundles} />
			{data.steps.length > 0 && <IntendedPathCard steps={data.steps} />}
		</div>
	);
}

function FailureBlock({
	data,
}: {
	data: Extract<SimulationResult, { ok: false }>;
}) {
	// Stage "cost" means the bundle passed plan + tx-build but
	// `eth_estimateGas` reverted — the broadcast would fail with the
	// same reason. Render it red and label it as a revert so it's
	// distinguishable from a build-time error (plan/tx stages, which
	// are bugs in the planner, not the user's intent).
	const isRevert = data.stage === "cost";
	const tone = isRevert
		? "border-rose-700/60 bg-rose-950/40 text-rose-200"
		: "border-amber-900/40 bg-amber-950/30 text-amber-200";
	const headerToneClass = isRevert ? "text-rose-300" : "text-amber-300";
	const header = isRevert ? "Will revert on broadcast" : `${data.stage} failed`;

	// Stack the revert card, the per-op triage card, and the
	// "Intended path" card as independent siblings — the path is
	// not part of the failure (it's the planner's neutral output)
	// so it shouldn't sit inside the rose-tinted error container.
	return (
		<div className="space-y-2">
			<div className={cn("space-y-2 rounded-md border p-2 text-[11px]", tone)}>
				<p
					className={cn(
						"font-semibold uppercase tracking-wide",
						headerToneClass,
					)}
				>
					{header}
				</p>
				<p className="break-words font-mono leading-snug">{data.reason}</p>
				{isRevert && (
					<p className="text-rose-300/70">
						Execute is disabled until the bundle simulates cleanly. Fix the
						intent (typically a balance/allowance issue) or remove the offending
						op.
					</p>
				)}
			</div>
			{data.triage && data.triage.length > 0 && (
				<TriageList triage={data.triage} />
			)}
			{data.steps && data.steps.length > 0 && (
				<IntendedPathCard steps={data.steps} />
			)}
		</div>
	);
}
